import { Injectable, inject, signal } from '@angular/core';
import { catchError, forkJoin, map, of, tap } from 'rxjs';
import Swal from 'sweetalert2';
import { NotificacionesApiService } from './notificaciones-api.service';
import { CatalogoItem, SeccionEnlace } from '../models/notificacion-institucional.model';

// Localidad con su provincia de origen (id y nombre) — se etiqueta acá
// pidiendo una provincia a la vez, ver cargarLocalidades. Necesario para
// distinguir localidades homónimas en distintas provincias (ej. "Merlo" en
// Córdoba y en San Luis) y para armar el contrato real de la notificación
// ({provinciaId, provincia, localidadId, localidad}, ver corrección #9).
export interface LocalidadCatalogoItem extends CatalogoItem {
  provinciaId: number;
  provinciaNombre: string;
}

// Nunca mostrarle al usuario el código HTTP crudo — mensaje descriptivo según
// el caso más común, con un genérico de respaldo. El texto puntual del
// backend (err.error?.mensaje) sigue teniendo prioridad cuando viene.
function mensajePorDefecto(status?: number): string {
  switch (status) {
    case 403: return 'No tenés permiso para acceder a este catálogo.';
    case 404: return 'No se encontró el catálogo solicitado.';
    case 0: return 'No se pudo conectar con el servidor. Verificá tu conexión e intentá de nuevo.';
    default: return 'Ha ocurrido un error. Por favor, inténtelo nuevamente.';
  }
}

// Store dedicado a los 4 catálogos externos de audiencia (coberturas, rangos
// etarios, provincias, localidades en cascada) — separado de NotificacionesStore
// a propósito: son datos de referencia compartidos por el formulario, no estado
// del ABM en sí (ver docs/specs/abmc-notificacion-institucional.md §8).
@Injectable({ providedIn: 'root' })
export class NotificacionesCatalogosStore {
  private readonly api = inject(NotificacionesApiService);

  // Cobertura ahora expone id (corrección #9, ver §9.10) — antes era una
  // lista de nombres plana (corrección #8).
  private readonly _coberturas = signal<CatalogoItem[]>([]);
  private readonly _provincias = signal<CatalogoItem[]>([]);
  private readonly _localidades = signal<LocalidadCatalogoItem[]>([]);
  private readonly _cargando = signal(false);
  private readonly _cargandoLocalidades = signal(false);
  private cargados = false;
  private readonly _seccionesEnlace = signal<SeccionEnlace[]>([]);
  private seccionesEnlaceCargadas = false;

  readonly coberturas = this._coberturas.asReadonly();
  readonly seccionesEnlace = this._seccionesEnlace.asReadonly();
  readonly provincias = this._provincias.asReadonly();
  readonly localidades = this._localidades.asReadonly();
  readonly cargando = this._cargando.asReadonly();
  readonly cargandoLocalidades = this._cargandoLocalidades.asReadonly();

  /** Idempotente: los catálogos base son de referencia, se cargan una sola vez por sesión. */
  cargarBase(): void {
    if (this.cargados) return;
    this.cargados = true;
    this._cargando.set(true);
    // Rango etario dejó de ser un catálogo (corrección #7: edadDesde/edadHasta).
    forkJoin({
      coberturas: this.api.listarCoberturas(),
      provincias: this.api.listarProvincias()
    }).pipe(
      tap(res => {
        this._coberturas.set(res.coberturas);
        this._provincias.set(res.provincias);
        this._cargando.set(false);
      }),
      catchError(err => {
        this.cargados = false;
        this._cargando.set(false);
        this.mostrarError(err);
        return of(null);
      })
    ).subscribe();
  }

  // Separado de cargarBase (fuera del forkJoin) a propósito: lo usa también la
  // auditoría, que no necesita coberturas/provincias, y si este endpoint falla
  // no tiene que arrastrar a los catálogos de audiencia. Idempotente igual que
  // cargarBase. No se hardcodea la lista: la administra Sistemas.
  cargarSeccionesEnlace(): void {
    if (this.seccionesEnlaceCargadas) return;
    this.seccionesEnlaceCargadas = true;
    this.api.listarSeccionesEnlace().pipe(
      tap(secciones => this._seccionesEnlace.set(secciones)),
      catchError(err => {
        this.seccionesEnlaceCargadas = false;
        this.mostrarError(err);
        return of(null);
      })
    ).subscribe();
  }

  // Una llamada por provincia (en vez de una sola con provinciaIds combinados)
  // a propósito: el endpoint no dice de qué provincia es cada localidad que
  // devuelve, y sin eso no se puede reconstruir {provincia, localidad} sin
  // ambigüedad cuando hay localidades homónimas en más de una provincia
  // seleccionada a la vez (ej. "Merlo" en Córdoba y San Luis) — ver
  // notificacion-nueva.component.ts.
  cargarLocalidades(provincias: CatalogoItem[]): void {
    if (provincias.length === 0) {
      this._localidades.set([]);
      return;
    }
    this._cargandoLocalidades.set(true);
    forkJoin(
      provincias.map(p =>
        this.api.listarLocalidades([p.id]).pipe(
          map((localidades): LocalidadCatalogoItem[] =>
            localidades.map(l => ({ ...l, provinciaId: p.id, provinciaNombre: p.nombre }))
          ),
          catchError(err => {
            this.mostrarError(err);
            return of([] as LocalidadCatalogoItem[]);
          })
        )
      )
    ).subscribe(resultados => {
      this._localidades.set(resultados.flat());
      this._cargandoLocalidades.set(false);
    });
  }

  private mostrarError(err: { status?: number; error?: { mensaje?: string } }): void {
    // 401: el authInterceptor ya hace logout() + navigate(['/login']) — no
    // duplicar con un popup técnico encima de esa redirección.
    if (err.status === 401) return;
    Swal.fire({
      title: 'Error',
      text: err.error?.mensaje ?? mensajePorDefecto(err.status),
      icon: 'error', confirmButtonText: 'Aceptar', confirmButtonColor: '#d33'
    });
  }
}
